/**
 * TrainingRevocationBadge — Batch 18-E18
 *
 * Read-only badge for the technician-facing Passport Learning Impact
 * tab. Closes the user-visible loop opened in 18-E17: when a manager
 * revokes verification, the 18-E13 Manager-Verified Training badge
 * disappears because manager_verified_at is cleared, leaving the
 * technician no explanation. This badge surfaces a small,
 * non-alarming "Verification revoked" indicator backed by the
 * append-only training_completion_verifications audit table.
 *
 * Distinguishes the three revoke sources per the 18-E15 / 18-E16
 * metadata schema:
 *   - Manual revoke (18-E15) — manager-initiated; no special flag,
 *     just the verification note.
 *   - Auto-revoke (18-E16 trigger) — status flipped to non-completable;
 *     metadata.auto_revoked = true.
 *   - One-shot reconciliation (18-E16 DO block) — system repaired a
 *     historically stale verified state; metadata.one_shot_reconciliation
 *     = true AND metadata.auto_revoked = true.
 *
 * Display rules:
 *   - Returns null when revokedAt is null/undefined.
 *   - Renders "Verification revoked · MMM d, yyyy".
 *   - When autoRevoked is true: prepends "Auto-revoked after status changed".
 *   - When oneShotReconciliation is true: prepends "System repaired stale
 *     verification" (overrides the auto-revoke prefix).
 *   - When revocationNote is present: muted italic line below the badge.
 *
 * Manager name is NOT displayed (UUID resolution to auth.users /
 * profiles requires an extra join the technician's RLS may not allow
 * — same conservative approach as 18-E13's TrainingVerificationBadge).
 *
 * This component does NOT:
 *   - call useRevokeTrainingCompletion or any mutation hook
 *   - write to training_consumption_sessions or
 *     training_completion_verifications
 *   - read or write training_effectiveness_events
 *   - emit a 'manager_verified' / 'revoked' / 'auto_revoked' event-type
 *     literal
 *   - reference session_token / extension_evidence / Companion code
 *   - implement any action button
 */

import { ShieldOff } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { getRevocationProvenanceLabel } from '@/utils/training/evidenceProvenanceLabels';

interface TrainingRevocationBadgeProps {
  revokedAt?: string | null;
  revocationNote?: string | null;
  autoRevoked?: boolean;
  oneShotReconciliation?: boolean;
  restoredConfidence?: string | null;
}

export function TrainingRevocationBadge({
  revokedAt,
  revocationNote,
  autoRevoked,
  oneShotReconciliation,
}: TrainingRevocationBadgeProps) {
  if (!revokedAt) return null;

  let dateLabel: string | null = null;
  try {
    dateLabel = format(new Date(revokedAt), 'MMM d, yyyy');
  } catch {
    dateLabel = null;
  }

  // Reason-line prefix. One-shot reconciliation takes precedence over
  // generic auto-revoke since it's a more specific signal.
  const reasonLine = oneShotReconciliation
    ? 'System repaired stale verification'
    : autoRevoked
    ? 'Auto-revoked after status changed'
    : null;

  // Batch 18-F15.1 — provenance line (Option B). Names the trust class
  // behind the revocation so the viewer can't confuse a human reversal
  // with an automated safety reversal or a system reconciliation.
  const provenanceLine = getRevocationProvenanceLabel({
    autoRevoked,
    oneShotReconciliation,
  });

  return (
    <div
      className="space-y-1"
      data-testid="training-revocation-badge"
    >
      <Badge
        variant="outline"
        className="border-muted-foreground/30 text-muted-foreground gap-1"
      >
        <ShieldOff className="h-3 w-3" />
        <span>Verification revoked</span>
        {dateLabel && (
          <span className="opacity-80 font-normal">· {dateLabel}</span>
        )}
      </Badge>
      {reasonLine && (
        <p className="text-xs text-muted-foreground">{reasonLine}</p>
      )}
      <p
        className="text-[10px] text-muted-foreground/80 uppercase tracking-wide"
        data-testid="training-revocation-provenance"
      >
        {provenanceLine}
      </p>
      {revocationNote && (
        <p className="text-xs text-muted-foreground italic">
          “{revocationNote}”
        </p>
      )}
    </div>
  );
}
